import type { ReactNode } from 'react';
import { cx } from '@/shared/ui/cx';

export type EmptyStateProps = {
  icon?: ReactNode;
  title: ReactNode;
  hint?: ReactNode;
  action?: ReactNode;
  /** Tighter padding for narrow rails and panels. */
  compact?: boolean;
  /*
   * The title is a real heading, not a styled div. An empty state often *is* the
   * page — /home with the engine unreachable renders nothing but this — and as
   * a div that left the document with no headings at all, so a screen reader had
   * no structural way in.
   *
   * 3 suits the common case, an empty state inside a section that already has
   * its own h2. One that replaces the whole page should pass 2.
   */
  headingLevel?: 2 | 3 | 4;
  className?: string;
};

export function EmptyState({
  icon,
  title,
  hint,
  action,
  compact = false,
  headingLevel = 3,
  className,
}: EmptyStateProps) {
  // `.empty__title` pins font-family, size and colour, and base.css zeroes
  // heading margins — so the level changes the outline, never the pixels.
  const Title = `h${headingLevel}` as 'h2' | 'h3' | 'h4';

  return (
    <div className={cx('empty', compact && 'empty--compact', className)}>
      {icon ? <div className="empty__icon">{icon}</div> : null}
      <Title className="empty__title">{title}</Title>
      {hint ? <div className="empty__hint">{hint}</div> : null}
      {action ? <div className="empty__action">{action}</div> : null}
    </div>
  );
}
